import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Folder, FolderPlus, ImageOff, Home, ChevronRight, Upload,
    ArrowLeft, MoreVertical, Edit2, Trash2, FolderInput, ShieldAlert, Download,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { imgSrc, imgSrcSet } from "../../lib/imgSrc";

/**
 * <ImageExplorer />
 *
 * File-explorer-style browser for the image library:
 *   • Top-level: shows the 6 categories as "drives" you click to enter.
 *   • Inside a category: shows direct sub-folders + direct images.
 *   • Breadcrumb (clickable) for navigation.
 *   • "New folder" + "Upload here" toolbar acts on the CURRENT location.
 *   • Drag-and-drop multi-file upload onto the grid.
 *   • Right-rail menu on each item for rename / move / delete.
 *   • Click an image to open the parent edit modal (provided via onEditImage).
 */
const ImageExplorer = ({ categories, folders, refreshFolders, onEditImage }) => {
    // Current location: ("", "") means "top-level / pick a category"
    const [categorySlug, setCategorySlug] = useState("");
    const [folderPath, setFolderPath] = useState(""); // empty = category root
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [batchOpen, setBatchOpen] = useState(false);
    const [batchImages, setBatchImages] = useState([]); // pending batch from a drop/upload
    const [moveOpen, setMoveOpen] = useState(null); // image being moved
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [homeNewFolderOpen, setHomeNewFolderOpen] = useState(false);
    const dropRef = useRef(null);

    // Direct children of current path in current category
    const directChildFolders = folders.filter((f) => {
        if (f.category_slug !== categorySlug) return false;
        if (!folderPath) {
            // root of category — return folders with no parent (depth 1)
            return !f.path.includes("/");
        }
        const prefix = `${folderPath}/`;
        if (!f.path.startsWith(prefix)) return false;
        const rest = f.path.slice(prefix.length);
        return rest.length > 0 && !rest.includes("/");
    });

    const loadImages = useCallback(async () => {
        if (!categorySlug) { setImages([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/admin/image-library", {
                params: { category: categorySlug, folder: folderPath },
            });
            setImages(data || []);
        } catch { setImages([]); }
        finally { setLoading(false); }
    }, [categorySlug, folderPath]);

    useEffect(() => { loadImages(); }, [loadImages]);

    // Close action menus on outside click
    useEffect(() => {
        const onDoc = () => setMenuOpenId(null);
        window.addEventListener("click", onDoc);
        return () => window.removeEventListener("click", onDoc);
    }, []);

    // ─── Navigation ───────────────────────────────────────────
    const enterCategory = (slug) => { setCategorySlug(slug); setFolderPath(""); };
    const enterFolder = (path) => { setFolderPath(path); };
    const goUp = () => {
        if (folderPath.includes("/")) {
            setFolderPath(folderPath.slice(0, folderPath.lastIndexOf("/")));
        } else if (folderPath) {
            setFolderPath("");
        } else {
            setCategorySlug("");
        }
    };

    const currentCategoryLabel = categories.find((c) => c.slug === categorySlug)?.label || categorySlug;
    const pathSegments = folderPath ? folderPath.split("/") : [];

    // ─── Inline folder creation ────────────────────────────────
    const promptNewFolder = async () => {
        if (!categorySlug) return;
        const name = window.prompt("New folder name");
        if (!name || !name.trim()) return;
        // Find parent_id for current path
        let parent_id = null;
        if (folderPath) {
            const parent = folders.find((f) => f.category_slug === categorySlug && f.path === folderPath);
            if (parent) parent_id = parent.id;
        }
        try {
            const body = { category_slug: categorySlug, name: name.trim() };
            if (parent_id) body.parent_id = parent_id;
            await api.post("/admin/image-folders", body);
            await refreshFolders();
            toast.success("Folder created");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create folder"); }
    };

    const renameFolderInPlace = async (f) => {
        const next = window.prompt("Rename folder", f.name);
        if (!next || next.trim() === f.name) return;
        try {
            await api.patch(`/admin/image-folders/${f.id}`, { name: next.trim() });
            await refreshFolders();
            await loadImages();
            toast.success("Folder renamed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    };

    const deleteFolderInPlace = async (f) => {
        if (!window.confirm(`Delete folder "${f.path}"? It must be empty (no images or sub-folders).`)) return;
        try {
            await api.delete(`/admin/image-folders/${f.id}`);
            await refreshFolders();
            toast.success("Folder removed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    // ─── Image actions ────────────────────────────────────────
    const moveImageTo = async (img, destFolderPath) => {
        try {
            await api.patch(`/admin/image-library/${img.id}`, { folder_path: destFolderPath || "" });
            await loadImages();
            toast.success(destFolderPath ? `Moved to ${destFolderPath}` : "Moved to category root");
            setMoveOpen(null);
        } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
    };

    const deleteImage = async (img) => {
        if (!window.confirm(`Delete "${img.title}"? Permanent.`)) return;
        try {
            await api.delete(`/admin/image-library/${img.id}`);
            setImages((prev) => prev.filter((x) => x.id !== img.id));
            toast.success("Image deleted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const downloadImage = async (img) => {
        try {
            // Use axios so we ship the auth cookie; ask for a blob so the browser
            // doesn't try to render the bytes.
            const res = await api.get(`/admin/image-library/${img.id}/download`, { responseType: "blob" });
            const blob = res.data;
            const url = window.URL.createObjectURL(blob);
            // Derive filename from Content-Disposition if present, else fall back to title
            const cd = res.headers["content-disposition"] || "";
            const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
            const fname = m ? m[1] : `${(img.title || "image").replace(/[^A-Za-z0-9._-]+/g, "_")}.jpg`;
            const a = document.createElement("a");
            a.href = url; a.download = fname;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 5000);
            toast.success("Download started");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Download failed");
        }
    };

    // ─── Upload (file input + drag-and-drop) ───────────────────
    const uploadFiles = async (fileList) => {
        if (!categorySlug) { toast.error("Pick a category first"); return; }
        const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
        if (files.length === 0) { toast.error("No image files selected"); return; }
        const oversize = files.filter((f) => f.size > 10 * 1024 * 1024);
        if (oversize.length) { toast.error(`${oversize.length} file(s) exceed 10MB — skipped`); }
        const valid = files.filter((f) => f.size <= 10 * 1024 * 1024);
        if (valid.length === 0) return;

        // Open the batch tracker immediately so admin sees progress
        setBatchImages(valid.map((f) => ({ status: "uploading", file_name: f.name, file_size: f.size })));
        setBatchOpen(true);

        // Upload sequentially to keep things deterministic & easy to debug
        const results = [];
        for (let i = 0; i < valid.length; i++) {
            const f = valid[i];
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const { data: img } = await api.post("/admin/image-library", {
                    url: up.url,
                    file_path: up.path,
                    category_slug: categorySlug,
                    folder_path: folderPath || undefined,
                    title: f.name.replace(/\.[^.]+$/, "") || "Untitled",
                    file_size: f.size,
                    mime_type: f.type || "image/jpeg",
                });
                results.push({ status: "ok", ...img });
            } catch (e) {
                results.push({ status: "error", file_name: f.name, error: e?.response?.data?.detail || "Upload failed" });
            }
            // Live-update each slot as it finishes
            setBatchImages((prev) => prev.map((row, idx) => idx === i ? results[i] : row));
        }
        await loadImages();
        const okCount = results.filter((r) => r.status === "ok").length;
        if (okCount > 0) toast.success(`${okCount} image(s) added`);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
    };
    const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const onDragLeave = (e) => {
        // Only consider it "leave" if leaving the drop area entirely (not children)
        if (e.target === dropRef.current) setDragOver(false);
    };

    // ─── Render ───────────────────────────────────────────────
    // Top-level: pick a category
    if (!categorySlug) {
        return (
            <div data-testid="image-explorer-home">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                    <p className="text-sm text-[#4A5568]">Pick a category to browse, or create a folder inside any category.</p>
                    <button
                        onClick={() => setHomeNewFolderOpen(true)}
                        data-testid="image-explorer-home-new-folder"
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-[#F4F1EA]"
                    >
                        <FolderPlus className="w-3 h-3" strokeWidth={2} /> New folder
                    </button>
                </div>
                <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {categories.filter((c) => c.is_active !== false).map((c) => (
                        <li key={c.id}>
                            <button
                                onClick={() => enterCategory(c.slug)}
                                data-testid={`image-explorer-cat-${c.slug}`}
                                className="w-full bg-white border border-black/10 rounded-2xl p-5 text-left hover:border-[#E53935] hover:shadow-md transition-all flex items-center gap-3"
                            >
                                <Folder className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                                <div>
                                    <p className="font-bold text-[#1C252E] text-sm">{c.label}</p>
                                    <p className="text-[11px] text-[#4A5568] font-mono">{c.slug}</p>
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
                {homeNewFolderOpen && (
                    <NewFolderModal
                        categories={categories.filter((c) => c.is_active !== false)}
                        folders={folders}
                        onClose={() => setHomeNewFolderOpen(false)}
                        onCreated={async (created) => {
                            setHomeNewFolderOpen(false);
                            await refreshFolders();
                            // Drop the admin into the new folder so they can immediately upload
                            setCategorySlug(created.category_slug);
                            setFolderPath(created.path);
                        }}
                    />
                )}
            </div>
        );
    }

    return (
        <div data-testid="image-explorer">
            {/* Breadcrumb + actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <nav className="flex items-center text-sm flex-wrap" data-testid="image-explorer-breadcrumb">
                    <button onClick={() => { setCategorySlug(""); setFolderPath(""); }} aria-label="All categories" className="inline-flex items-center gap-1 hover:text-[#E53935] text-[#4A5568]" data-testid="image-explorer-crumb-home">
                        <Home className="w-3.5 h-3.5" strokeWidth={1.5} /> Library
                    </button>
                    <ChevronRight className="w-3 h-3 mx-1 text-[#4A5568]" strokeWidth={2} />
                    <button onClick={() => enterFolder("")} className={`hover:text-[#E53935] ${!folderPath ? "font-bold text-[#1C252E]" : "text-[#4A5568]"}`} data-testid={`image-explorer-crumb-${categorySlug}`}>
                        {currentCategoryLabel}
                    </button>
                    {pathSegments.map((seg, i) => {
                        const targetPath = pathSegments.slice(0, i + 1).join("/");
                        const isLast = i === pathSegments.length - 1;
                        return (
                            <React.Fragment key={targetPath}>
                                <ChevronRight className="w-3 h-3 mx-1 text-[#4A5568]" strokeWidth={2} />
                                <button
                                    onClick={() => enterFolder(targetPath)}
                                    data-testid={`image-explorer-crumb-seg-${targetPath}`}
                                    className={`hover:text-[#E53935] ${isLast ? "font-bold text-[#1C252E]" : "text-[#4A5568]"}`}
                                >
                                    {seg}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </nav>
                <div className="flex flex-wrap gap-2">
                    {(folderPath || categorySlug) && (
                        <button onClick={goUp} data-testid="image-explorer-up" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-[#F4F1EA]">
                            <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Up
                        </button>
                    )}
                    <button onClick={promptNewFolder} data-testid="image-explorer-new-folder" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-[#F4F1EA]">
                        <FolderPlus className="w-3 h-3" strokeWidth={2} /> New folder
                    </button>
                    <label className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-[#E53935] text-white hover:bg-[#D32F2F] cursor-pointer" data-testid="image-explorer-upload">
                        <Upload className="w-3 h-3" strokeWidth={2} /> Upload here
                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="image-explorer-upload-input" />
                    </label>
                </div>
            </div>

            {/* Drop-zone grid */}
            <div
                ref={dropRef}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                data-testid="image-explorer-dropzone"
                className={`relative min-h-[320px] rounded-2xl border-2 ${dragOver ? "border-dashed border-[#E53935] bg-[#E53935]/5" : "border-dashed border-black/10 bg-transparent"} transition-colors p-4`}
            >
                {dragOver && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="image-explorer-dropzone-overlay">
                        <p className="text-lg font-bold text-[#E53935] inline-flex items-center gap-2">
                            <Upload className="w-5 h-5" strokeWidth={2} /> Drop images to upload to <span className="font-mono text-sm">{folderPath || currentCategoryLabel}</span>
                        </p>
                    </div>
                )}

                {loading ? (
                    <p className="text-sm text-[#4A5568] p-4">Loading…</p>
                ) : (directChildFolders.length === 0 && images.length === 0) ? (
                    <div className="text-center py-16">
                        <ImageOff className="w-10 h-10 mx-auto mb-3 text-[#4A5568]/40" strokeWidth={1.5} />
                        <p className="text-sm text-[#4A5568]">This folder is empty. <strong>Drag images here</strong> to upload, or click "New folder" / "Upload here" above.</p>
                    </div>
                ) : (
                    <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="image-explorer-grid">
                        {directChildFolders.map((f) => (
                            <li key={f.id} className="relative">
                                <button
                                    onDoubleClick={() => enterFolder(f.path)}
                                    onClick={() => enterFolder(f.path)}
                                    data-testid={`image-explorer-folder-${f.path}`}
                                    className="w-full bg-white border border-black/10 rounded-2xl p-4 text-left hover:border-[#E53935] hover:shadow-md transition-all flex items-center gap-3"
                                >
                                    <Folder className="w-7 h-7 text-[#E53935] flex-shrink-0" strokeWidth={1.5} />
                                    <div className="flex-1 overflow-hidden">
                                        <p className="font-bold text-sm text-[#1C252E] truncate">{f.name}</p>
                                        <p className="text-[10px] text-[#4A5568] font-mono truncate">{f.path}</p>
                                    </div>
                                </button>
                                <div className="absolute top-2 right-2 flex gap-1">
                                    <button onClick={() => renameFolderInPlace(f)} aria-label="Rename folder" data-testid={`image-explorer-folder-rename-${f.path}`} className="p-1 bg-white/95 rounded-full hover:bg-black/5"><Edit2 className="w-3 h-3 text-[#4A5568]" strokeWidth={2} /></button>
                                    <button onClick={() => deleteFolderInPlace(f)} aria-label="Delete folder" data-testid={`image-explorer-folder-delete-${f.path}`} className="p-1 bg-white/95 rounded-full hover:bg-red-50"><Trash2 className="w-3 h-3 text-[#E53935]" strokeWidth={2} /></button>
                                </div>
                            </li>
                        ))}
                        {images.map((img) => (
                            <li key={img.id} className="relative group" data-testid={`image-explorer-image-${img.id}`}>
                                <button
                                    onDoubleClick={() => onEditImage?.(img)}
                                    onClick={() => onEditImage?.(img)}
                                    className="w-full bg-white border border-black/10 rounded-2xl overflow-hidden hover:shadow-md transition-all"
                                >
                                    <div className="w-full h-32 bg-[#F4F1EA] flex items-center justify-center overflow-hidden">
                                        {img.url ? (
                                            <img
                                                src={imgSrc(img.thumbnail_url || img.url, 320, 70)}
                                                srcSet={imgSrcSet(img.thumbnail_url || img.url, 320, 70)}
                                                alt={img.title}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <ImageOff className="w-6 h-6 text-[#4A5568]/40" strokeWidth={1.5} />
                                        )}
                                    </div>
                                    <div className="p-2.5">
                                        <p className="text-xs font-bold text-[#1C252E] line-clamp-1" title={img.title}>{img.title}</p>
                                        <div className="flex items-center gap-1 mt-1">
                                            {img.consent_required && img.consent_status !== "granted" && img.consent_status !== "not_required" && (
                                                <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-900 rounded-full px-1.5 py-0.5">
                                                    <ShieldAlert className="w-2.5 h-2.5" strokeWidth={2} /> {img.consent_status}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                                <div className="absolute top-2 right-2">
                                    <button onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === img.id ? null : img.id); }} aria-label="Actions" data-testid={`image-explorer-image-menu-${img.id}`} className="p-1.5 bg-white/95 rounded-full shadow-sm hover:bg-black/5">
                                        <MoreVertical className="w-3.5 h-3.5 text-[#4A5568]" strokeWidth={2} />
                                    </button>
                                    {menuOpenId === img.id && (
                                        <div onClick={(e) => e.stopPropagation()} data-testid={`image-explorer-image-menu-open-${img.id}`} className="absolute right-0 top-8 z-20 bg-white border border-black/10 rounded-xl shadow-lg py-1 w-40">
                                            <button onClick={() => { onEditImage?.(img); setMenuOpenId(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 flex items-center gap-2" data-testid={`image-explorer-image-edit-${img.id}`}><Edit2 className="w-3 h-3" strokeWidth={2} /> Edit details</button>
                                            <button onClick={() => { downloadImage(img); setMenuOpenId(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 flex items-center gap-2" data-testid={`image-explorer-image-download-${img.id}`}><Download className="w-3 h-3" strokeWidth={2} /> Download</button>
                                            <button onClick={() => { setMoveOpen(img); setMenuOpenId(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 flex items-center gap-2" data-testid={`image-explorer-image-move-${img.id}`}><FolderInput className="w-3 h-3" strokeWidth={2} /> Move to…</button>
                                            <button onClick={() => { deleteImage(img); setMenuOpenId(null); }} className="w-full text-left px-3 py-1.5 text-xs text-[#E53935] hover:bg-red-50 flex items-center gap-2" data-testid={`image-explorer-image-delete-${img.id}`}><Trash2 className="w-3 h-3" strokeWidth={2} /> Delete</button>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {moveOpen && (
                <MoveImageModal
                    image={moveOpen}
                    folders={folders.filter((f) => f.category_slug === moveOpen.category_slug)}
                    categoryLabel={categories.find((c) => c.slug === moveOpen.category_slug)?.label || moveOpen.category_slug}
                    onClose={() => setMoveOpen(null)}
                    onMove={(path) => moveImageTo(moveOpen, path)}
                />
            )}

            {batchOpen && (
                <BatchUploadModal
                    rows={batchImages}
                    folderPath={folderPath}
                    categoryLabel={currentCategoryLabel}
                    onClose={() => { setBatchOpen(false); setBatchImages([]); }}
                    onEdit={(img) => { setBatchOpen(false); setBatchImages([]); onEditImage?.(img); }}
                />
            )}
        </div>
    );
};

// ============================================================
// Move-image modal — pick a destination folder within the image's category
// ============================================================
const MoveImageModal = ({ image, folders, categoryLabel, onClose, onMove }) => {
    const [dest, setDest] = useState(image.folder_path || "");
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="move-image-modal">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
                <h2 className="font-heading text-lg text-[#1C252E]">Move "{image.title}"</h2>
                <p className="text-xs text-[#4A5568]">Pick a destination folder inside <strong>{categoryLabel}</strong>. To move across categories, edit the image's category first.</p>
                <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="move-image-dest" className="w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm">
                    <option value="">— Category root —</option>
                    {folders.map((f) => <option key={f.id} value={f.path}>{f.path}</option>)}
                </select>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-full text-sm hover:bg-black/5">Cancel</button>
                    <button onClick={() => onMove(dest)} data-testid="move-image-save" className="px-4 py-2 rounded-full bg-[#E53935] hover:bg-[#D32F2F] text-white text-sm font-medium">Move</button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Batch upload review modal — opens during/after multi-file upload
// ============================================================
const BatchUploadModal = ({ rows, folderPath, categoryLabel, onClose, onEdit }) => {
    const okRows = rows.filter((r) => r.status === "ok");
    const errRows = rows.filter((r) => r.status === "error");
    const uploadingRows = rows.filter((r) => r.status === "uploading");
    const needsConsent = okRows.filter((r) => !r.consent_required); // every fresh upload defaults to not_required — admins decide here
    const stillRunning = uploadingRows.length > 0;
    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="batch-upload-modal">
            <div className="absolute inset-0 bg-black/40" onClick={stillRunning ? undefined : onClose} aria-hidden />
            <div className="relative w-full max-w-2xl bg-[#FDFBF7] h-full overflow-y-auto shadow-2xl">
                <header className="sticky top-0 bg-[#FDFBF7]/95 backdrop-blur border-b border-black/10 px-6 py-4 z-10">
                    <h2 className="font-heading text-lg text-[#1C252E]">Batch upload — {rows.length} file{rows.length === 1 ? "" : "s"}</h2>
                    <p className="text-xs text-[#4A5568] mt-1">
                        Destination: <strong>{categoryLabel}{folderPath ? ` / ${folderPath}` : ""}</strong>
                    </p>
                </header>
                <div className="p-6 space-y-3">
                    {stillRunning && <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">{uploadingRows.length} still uploading… you can close this once they finish.</p>}
                    {needsConsent.length > 0 && !stillRunning && (
                        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-start gap-2">
                            <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2} />
                            <span>Click <strong>Add consent</strong> on any photo that shows an identifiable person — until consent is granted, the image will NOT appear in the public picker.</span>
                        </p>
                    )}
                    <ul className="space-y-2" data-testid="batch-upload-rows">
                        {rows.map((r, i) => (
                            <li key={i} className="flex items-center gap-3 bg-white border border-black/10 rounded-xl p-3" data-testid={`batch-upload-row-${i}`}>
                                <div className="w-14 h-14 bg-[#F4F1EA] rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {r.url ? (
                                        <img
                                            src={imgSrc(r.url, 128, 70)}
                                            srcSet={imgSrcSet(r.url, 128, 70)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <ImageOff className="w-5 h-5 text-[#4A5568]/40" strokeWidth={1.5} />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-[#1C252E] truncate">{r.title || r.file_name}</p>
                                    {r.status === "uploading" && <p className="text-[11px] text-[#4A5568]">Uploading…</p>}
                                    {r.status === "ok" && <p className="text-[11px] text-green-700">Uploaded</p>}
                                    {r.status === "error" && <p className="text-[11px] text-red-700">Failed · {r.error}</p>}
                                </div>
                                {r.status === "ok" && (
                                    <button onClick={() => onEdit(r)} data-testid={`batch-upload-edit-${i}`} className="text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-[#F4F1EA] whitespace-nowrap">
                                        Edit / Add consent
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                    {errRows.length > 0 && <p className="text-xs text-red-700">{errRows.length} upload(s) failed.</p>}
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={onClose} disabled={stillRunning} data-testid="batch-upload-done" className="px-5 py-2 rounded-full bg-[#1C252E] text-white text-sm font-medium hover:bg-black disabled:opacity-60">
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// New-folder modal — shown from Library home so admins can create a folder
// in any category without first navigating into that category.
// ============================================================
const NewFolderModal = ({ categories, folders, onClose, onCreated }) => {
    const [categorySlug, setCategorySlug] = useState(categories[0]?.slug || "");
    const [parentId, setParentId] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    const scoped = folders.filter((f) => f.category_slug === categorySlug);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim() || !categorySlug) {
            toast.error("Pick a category and enter a folder name");
            return;
        }
        setBusy(true);
        try {
            const body = { category_slug: categorySlug, name: name.trim() };
            if (parentId) body.parent_id = parentId;
            const { data } = await api.post("/admin/image-folders", body);
            toast.success("Folder created");
            onCreated(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-folder-modal">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
            <form onSubmit={submit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
                <h2 className="font-heading text-lg text-[#1C252E]">New folder</h2>
                <p className="text-xs text-[#4A5568]">Folders live inside a category. Pick where this folder belongs.</p>

                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Category</span>
                    <select
                        value={categorySlug}
                        onChange={(e) => { setCategorySlug(e.target.value); setParentId(""); }}
                        data-testid="new-folder-modal-category"
                        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm"
                    >
                        {categories.map((c) => <option key={c.id} value={c.slug}>{c.label}</option>)}
                    </select>
                </label>

                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Inside</span>
                    <select
                        value={parentId}
                        onChange={(e) => setParentId(e.target.value)}
                        data-testid="new-folder-modal-parent"
                        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm"
                    >
                        <option value="">— Category root —</option>
                        {scoped.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
                    </select>
                </label>

                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Folder name</span>
                    <input
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Spring 2026"
                        data-testid="new-folder-modal-name"
                        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm"
                    />
                </label>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm hover:bg-black/5">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="new-folder-modal-create" className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-[#E53935] hover:bg-[#D32F2F] text-white text-sm font-medium disabled:opacity-60">
                        <FolderPlus className="w-3.5 h-3.5" strokeWidth={2} /> {busy ? "Creating…" : "Create folder"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ImageExplorer;
